// src/components/AdminTierOverride.js
// Admin-only floating dropdown for testing different subscription tiers
// Allows admins to view the app as Free, Pro, or Elite users without changing the database

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminTierOverride = ({ currentTier, onTierChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const tiers = ['free', 'pro', 'elite', 'seeker'];

  const getTierColor = (tier) => {
    switch (tier) {
      case 'elite':
        return '#7c3aed'; // Purple
      case 'pro':
        return '#2563EB'; // Blue
      case 'seeker':
        return '#bb9749'; // Gold
      case 'free':
      default:
        return '#6B7280'; // Gray
    }
  };

  const getTierLabel = (tier) => {
    if (tier === 'seeker') return 'Client / Seeker';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 9999,
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
        }}
        style={{
          padding: '10px 16px',
          borderRadius: '8px',
          backgroundColor: '#1F2937',
          color: '#FFFFFF',
          border: 'none',
          fontSize: '12px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          transition: 'all 0.2s ease',
        }}
      >
        {/* Tier Indicator Dot */}
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: getTierColor(currentTier),
            transition: 'background-color 0.2s ease',
          }}
        />
        <span>Viewing as: {getTierLabel(currentTier)}</span>
        <span style={{ fontSize: '10px', opacity: 0.7 }}>
          {isOpen ? '▲' : '▼'}
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            right: 0,
            marginBottom: '8px',
            backgroundColor: '#1F2937',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            minWidth: '160px',
            border: '1px solid #374151',
          }}
        >
          {tiers.map((tier) => (
            <button
              key={tier}
              onClick={() => {
                onTierChange(tier);
                setIsOpen(false);
              }}
              onMouseEnter={(e) => {
                if (tier !== currentTier) {
                  e.currentTarget.style.backgroundColor = '#374151';
                }
              }}
              onMouseLeave={(e) => {
                if (tier !== currentTier) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                width: '100%',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: tier === currentTier ? '#374151' : 'transparent',
                color: '#FFFFFF',
                fontSize: '12px',
                fontWeight: tier === currentTier ? '600' : '500',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background-color 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Tier Indicator Dot */}
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: getTierColor(tier),
                    flexShrink: 0,
                  }}
                />
                <span>{getTierLabel(tier)}</span>
              </div>
              {/* Checkmark for current tier */}
              {tier === currentTier && (
                <span style={{ fontSize: '14px', opacity: 0.8 }}>✓</span>
              )}
            </button>
          ))}

          {/* Divider */}
          <div style={{
            height: '1px',
            backgroundColor: '#374151',
            margin: '4px 0',
          }} />

          {/* Preview Homepage Button */}
          <button
            onClick={() => {
              setIsOpen(false);
              navigate('/home-preview');
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#374151'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '10px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              color: '#9CA3AF',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background-color 0.15s ease',
            }}
          >
            <span style={{ fontSize: '13px' }}>&#x1F50D;</span>
            <span>Preview Homepage</span>
          </button>

          {/* Info Text */}
          <div style={{
            padding: '8px 16px',
            fontSize: '10px',
            color: '#9CA3AF',
            lineHeight: '1.4',
          }}>
            Admin only • Changes not saved to database
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTierOverride;
